import {
  BELT_NAMES,
  BELTS,
  type Belt,
  type CertificateDto,
  type CurriculumDto,
  type SignatureStatusDto,
  type UserDto,
} from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type PointerEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from './DataTable';
import { Card, ConfirmButton, ErrorMessage, formatDate } from './ui';

export const signatureKey = ['me', 'signature'];
export const useSignatureStatus = () =>
  useQuery({
    queryKey: signatureKey,
    queryFn: () => api.request<SignatureStatusDto>('/me/signature'),
  });

/** The belt as a small bar in its colours (grey/white has a white stripe through it). */
export function BeltSwatch({ belt }: { belt: Belt }) {
  return <span className={`belt-swatch belt-${belt}`} aria-hidden="true" />;
}

/** Strips the `data:image/png;base64,` prefix from a canvas export. */
const pngBase64 = (canvas: HTMLCanvasElement) => canvas.toDataURL('image/png').split(',')[1] ?? '';

/**
 * The signed-in instructor's or owner's signature for certificates, drawn with a mouse, finger or
 * stylus. Certificates keep a copy, so a new signature only applies to certificates issued later.
 */
export function SignatureCard() {
  const status = useSignatureStatus();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const save = useApiMutation<void, SignatureStatusDto>(
    () => ({ path: '/me/signature', method: 'PUT', body: { image: pngBase64(canvas.current!) } }),
    [signatureKey],
  );
  const remove = useApiMutation<void>(
    () => ({ path: '/me/signature', method: 'DELETE' }),
    [signatureKey],
  );

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const c = canvas.current!;
    const r = c.getBoundingClientRect();
    return [
      ((e.clientX - r.left) * c.width) / r.width,
      ((e.clientY - r.top) * c.height) / r.height,
    ];
  };
  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvas.current!.getContext('2d')!;
    canvas.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    const [x, y] = point(e);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111113';
    ctx.beginPath();
    ctx.moveTo(x!, y!);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvas.current!.getContext('2d')!;
    const [x, y] = point(e);
    ctx.lineTo(x!, y!);
    ctx.stroke();
    setDrawn(true);
  };
  const clear = () => {
    const c = canvas.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };

  return (
    <Card title="Your signature">
      <p className="muted">
        Certificates you issue carry this signature. Draw it below with your mouse, finger or
        stylus.
      </p>
      <ErrorMessage error={status.error ?? save.error ?? remove.error} />
      {status.data?.hasSignature ? (
        <div className="signature-current">
          <img
            src={`/api/v1/me/signature/image?v=${encodeURIComponent(status.data.updatedAt ?? '')}`}
            alt="Your current signature"
          />
          <span className="muted">Saved {formatDate(status.data.updatedAt)}</span>
        </div>
      ) : status.data ? (
        <div className="alert info" role="status">
          You haven&rsquo;t added a signature yet. You need one to issue certificates.
        </div>
      ) : null}
      <canvas
        ref={canvas}
        className="signature-pad"
        width={600}
        height={180}
        aria-label="Signature drawing area"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={() => (drawing.current = false)}
        onPointerCancel={() => (drawing.current = false)}
      />
      <div className="btn-row">
        <button
          type="button"
          className="btn primary"
          disabled={!drawn || save.isPending}
          onClick={() => save.mutate(undefined, { onSuccess: clear })}
        >
          Save signature
        </button>
        <button type="button" className="btn" disabled={!drawn} onClick={clear}>
          Clear
        </button>
        {status.data?.hasSignature ? (
          <ConfirmButton confirmLabel="Remove signature?" onConfirm={() => remove.mutate()}>
            Remove
          </ConfirmButton>
        ) : null}
      </div>
    </Card>
  );
}

const rank = (belt: Belt) => BELTS.indexOf(belt);

/** Whether a student has mastered their whole curriculum and doesn't hold its belt yet. */
export function readyForPromotion(student: UserDto, curriculum: CurriculumDto): boolean {
  const { mastered, total } = curriculum.progress.overall;
  return (
    total > 0 && mastered >= total && rank(student.belt ?? 'white') < rank(curriculum.nextBelt.belt)
  );
}

/** A student's belt and certificates, with Issue certificate once they're ready. */
export function PromotionCard({
  student,
  curriculum,
}: {
  student: UserDto;
  curriculum: CurriculumDto;
}) {
  const { user } = useAuth();
  const key = ['certificates', student.id];
  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      api
        .request<{ items: CertificateDto[] }>(`/certificates?studentId=${student.id}`)
        .then((r) => r.items),
  });
  const signature = useSignatureStatus();
  const invalidate = [key, ['students', student.id], ['students'], ['promotions']];
  const issue = useApiMutation<void, { certificate: CertificateDto }>(
    () => ({ path: '/certificates', method: 'POST', body: { studentId: student.id } }),
    invalidate,
  );
  const withdraw = useApiMutation<string>(
    (id) => ({ path: `/admin/certificates/${id}`, method: 'DELETE' }),
    invalidate,
  );
  const ready = readyForPromotion(student, curriculum);
  const belt = student.belt ?? 'white';

  return (
    <Card title="Belt and certificates">
      <p>
        <BeltSwatch belt={belt} /> <strong>{BELT_NAMES[belt]}</strong>
        {ready ? (
          <span className="muted">
            {' '}
            · ready for {curriculum.nextBelt.name}: every technique is mastered
          </span>
        ) : rank(belt) < rank(curriculum.nextBelt.belt) ? (
          <span className="muted">
            {' '}
            · {curriculum.progress.overall.percent}% of the way to {curriculum.nextBelt.name}
          </span>
        ) : null}
      </p>
      <ErrorMessage error={list.error ?? issue.error ?? withdraw.error} />
      {ready && student.status === 'active' ? (
        signature.data && !signature.data.hasSignature ? (
          <div className="alert info" role="status">
            To issue {student.name}&rsquo;s certificate, first{' '}
            <Link to="/promotions">add your signature</Link>.
          </div>
        ) : (
          <ConfirmButton
            className="btn primary"
            confirmLabel={`Promote to ${curriculum.nextBelt.name} and sign?`}
            onConfirm={() => issue.mutate()}
          >
            Issue {curriculum.nextBelt.name} certificate
          </ConfirmButton>
        )
      ) : null}
      {list.data && list.data.length > 0 ? (
        <DataTable
          rows={list.data}
          rowKey={(c) => c.id}
          columns={[
            {
              key: 'belt',
              header: 'Belt',
              render: (c) => (
                <span>
                  <BeltSwatch belt={c.belt} /> {c.beltName}
                </span>
              ),
            },
            { key: 'issued', header: 'Issued', render: (c) => formatDate(c.issuedAt) },
            { key: 'by', header: 'Signed by', render: (c) => c.issuerName },
            {
              key: 'actions',
              header: '',
              render: (c) => (
                <div className="btn-row">
                  <Link className="btn small" to={`/certificates/${c.id}`}>
                    View &amp; print
                  </Link>
                  {user?.role === 'owner' ? (
                    <ConfirmButton
                      confirmLabel="Withdraw certificate?"
                      onConfirm={() => withdraw.mutate(c.id)}
                    >
                      Withdraw
                    </ConfirmButton>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      ) : null}
    </Card>
  );
}

/** The certificate itself, laid out for printing on a landscape page. */
export function CertificateView({ certificate: c }: { certificate: CertificateDto }) {
  return (
    <article className="certificate" aria-label={`${c.beltName} certificate for ${c.studentName}`}>
      <div className="certificate-inner">
        {c.logoUrl ? <img className="certificate-logo" src={c.logoUrl} alt={c.gymName} /> : null}
        <p className="certificate-gym">{c.gymName}</p>
        <h2 className="certificate-title">Certificate of Promotion</h2>
        <p className="certificate-lead">This certifies that</p>
        <p className="certificate-name">{c.studentName}</p>
        <p className="certificate-lead">has been promoted to the rank of</p>
        <p className="certificate-belt">
          <BeltSwatch belt={c.belt} /> {c.beltName}
        </p>
        <div className="certificate-footer">
          <div>
            <p className="certificate-date">
              {new Date(c.issuedAt).toLocaleDateString(undefined, { dateStyle: 'long' })}
            </p>
            <p className="certificate-caption">Date</p>
          </div>
          <div>
            <img
              className="certificate-signature"
              src={`/api/v1${c.signatureUrl}`}
              alt={`Signature of ${c.issuerName}`}
            />
            <p className="certificate-caption">
              {c.issuerName}, {c.issuerRole === 'owner' ? 'Owner' : 'Instructor'}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
